import { Component, DestroyRef, effect, inject, signal, ViewEncapsulation } from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { type NotificationMessage, NotificationService } from './notification.service';
import { IconDirective } from 'common/icon/icon.directive';
import { ButtonDirective } from 'common/button/button.directive';

@Component({
  selector: 'comp-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [MatMenu, MatMenuTrigger, TranslateModule, IconDirective, ButtonDirective],
})
export class NotificationComponent {
  private static readonly NOTIFICATION_PREVIEW_DURATION_MS = 5_000;
  private static readonly NOTIFICATION_PREVIEW_CLOSE_DURATION_MS = 400;

  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationService = inject(NotificationService);

  protected readonly notifications = this.notificationService.notifications;
  protected readonly notificationPreview = signal<NotificationMessage[]>([]);
  protected readonly notificationPreviewClosing = signal(false);

  private notificationPreviewTimeout: ReturnType<typeof setTimeout> | undefined;
  private notificationPreviewCloseTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    effect(() => {
      const preview = this.notificationService.notificationPreview();
      if (preview.length === 0) {
        return;
      }

      this.clearNotificationPreviewTimeouts();
      this.notificationPreviewClosing.set(false);
      this.notificationPreview.set(preview);

      this.notificationPreviewTimeout = setTimeout(() => {
        this.notificationPreviewClosing.set(true);
        this.notificationPreviewCloseTimeout = setTimeout(() => {
          this.notificationPreview.set([]);
          this.notificationPreviewClosing.set(false);
        }, NotificationComponent.NOTIFICATION_PREVIEW_CLOSE_DURATION_MS);
      }, NotificationComponent.NOTIFICATION_PREVIEW_DURATION_MS);
    });

    this.destroyRef.onDestroy(() => this.clearNotificationPreviewTimeouts());
  }

  private clearNotificationPreviewTimeouts(): void {
    if (this.notificationPreviewTimeout != null) {
      clearTimeout(this.notificationPreviewTimeout);
      this.notificationPreviewTimeout = undefined;
    }

    if (this.notificationPreviewCloseTimeout != null) {
      clearTimeout(this.notificationPreviewCloseTimeout);
      this.notificationPreviewCloseTimeout = undefined;
    }
  }
}
